import { create } from 'zustand'

export type ThemePreference = 'light' | 'dark' | 'system'
type ResolvedTheme = 'light' | 'dark'

const STORAGE_KEY = 'itpu.theme'
const DARK_QUERY = '(prefers-color-scheme: dark)'

function readPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'light' || stored === 'dark') return stored
  } catch {
    /* private mode — fall back to the OS preference for this session */
  }
  return 'system'
}

function resolve(preference: ThemePreference): ResolvedTheme {
  if (preference !== 'system') return preference
  return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light'
}

/**
 * The resolved theme is always stamped on <html>, never left to a CSS media
 * query. That keeps the palette to a single dark block in tokens.css instead of
 * two that have to be kept in step.
 */
function apply(preference: ThemePreference): void {
  document.documentElement.dataset.theme = resolve(preference)
  try {
    if (preference === 'system') localStorage.removeItem(STORAGE_KEY)
    else localStorage.setItem(STORAGE_KEY, preference)
  } catch {
    /* private mode — the choice lasts for this session only */
  }
}

interface ThemeStore {
  preference: ThemePreference
  setPreference: (preference: ThemePreference) => void
}

export const useTheme = create<ThemeStore>((set) => {
  const initial = readPreference()
  apply(initial)

  // While the user is on "system", follow the OS if it changes underneath us.
  window.matchMedia(DARK_QUERY).addEventListener('change', () => {
    if (useTheme.getState().preference === 'system') apply('system')
  })

  return {
    preference: initial,
    setPreference: (preference) => {
      apply(preference)
      set({ preference })
    },
  }
})
